import { useContext } from 'react';
import { isEqual, mapValues } from 'lodash';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { toastError } from 'common/toast';
import { Routes } from 'common/enum/routes';
import { useCurrentUser } from './useCurrentUser';
import { getFortiCloudData } from 'api/forticloud';
import { SystemSettingsContext } from 'components/providers/SystemSettingsProvider';

import { APIKeys } from 'common/enum/api-keys.enum';
import { FortiCloudCommonData, FortiCloudData } from 'common/types/forticloud';

export const useFortiCloud = () => {
  const location = useLocation();
  const { isRoot } = useCurrentUser();
  const urlParams = new URLSearchParams(location.search);
  const disableSidebar = urlParams.get('disableSidebar');
  const isDisableSidebar = isEqual(disableSidebar, 'true');

  const {
    data: { isFortiCloudMode },
  } = useContext(SystemSettingsContext);

  const isInIframe = window.self !== window.top;
  const isShowSidebarAndNavigationBar = isInIframe
    ? !!disableSidebar && !isDisableSidebar
    : !disableSidebar || !isDisableSidebar;

  const isFortiCloudModeAndNotIsRoot = isFortiCloudMode && !isRoot;
  const isFetchFortiCloudData =
    isFortiCloudModeAndNotIsRoot && isShowSidebarAndNavigationBar;

  const { data, refetch, isFetching } = useQuery<FortiCloudData>({
    queryFn: () => fetchData(),
    queryKey: [APIKeys.FORTICLOUD],
    enabled: isFetchFortiCloudData,
  });

  const fetchData = async (): Promise<FortiCloudData> => {
    try {
      const {
        content: {
          user_id: userId,
          idp_name: idpName,
          user_email: userEmail,
          selected_account_id_user_id: selectedAccountIdUserId,
          accounts_ids_users_ids_to_accounts_permissions:
            accountsIdsUsersIdsToAccountsPermissions,
        },
      } = await getFortiCloudData();

      const isAccountSelectionPage =
        location.pathname === Routes.ACCOUNT_SELECTION;

      if (!selectedAccountIdUserId && !isAccountSelectionPage) {
        window.location.replace(Routes.ACCOUNT_SELECTION);
      }

      const formattedAccountsIdsUsersIdsToAccountsPermissions = mapValues(
        accountsIdsUsersIdsToAccountsPermissions,
        ({
          role_name,
          account_id,
          common_data,
          is_master_user,
          user_display_info,
        }: {
          account_id: number;
          role_name: string;
          is_master_user: boolean;
          common_data: FortiCloudCommonData;
          user_display_info: { name: string; value: string }[];
        }) => ({
          roleName: role_name,
          accountId: account_id,
          commonData: common_data,
          isMasterUser: is_master_user,
          userDisplayInfo: user_display_info,
        }),
      );

      return {
        userId,
        idpName,
        userEmail,
        selectedAccountIdUserId,
        accountsIdsUsersIdsToAccountsPermissions:
          formattedAccountsIdsUsersIdsToAccountsPermissions,
      };
    } catch (error) {
      toastError('Failed to fetch FortiCloud data', error);

      throw error;
    }
  };

  return {
    data,
    refetch,
    isLoading: isFetching,
  };
};
